class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close(evt));
    // Delegated overlay click: #CartDrawer-Overlay lives inside #CartDrawer,
    // whose innerHTML is replaced wholesale on cart re-renders (renderContents,
    // gift reconcile) — a listener bound directly to the overlay dies with it.
    this.addEventListener('click', (evt) => {
      if (evt.target.id === 'CartDrawer-Overlay') this.close(evt);
    });
    // Enter in a quantity field: apply the typed quantity, don't submit.
    // The form's only submit control is Check out (the Update button is
    // <noscript>), so implicit submission sent shoppers straight to checkout
    // mid-edit. blur() commits the value (fires the native `change` that
    // cart.js listens to); focus() puts the caret back so they can keep going.
    this.addEventListener('keydown', (evt) => {
      if (evt.key !== 'Enter' || evt.isComposing) return;
      const input = evt.target.closest && evt.target.closest('.quantity__input');
      if (!input || !this.contains(input)) return;
      evt.preventDefault();
      input.blur();
      input.focus();
    });
    // `formdata` doesn't bubble, so capture it on the host (the form itself
    // is replaced on every re-render).
    this.addEventListener('formdata', (evt) => this.onCheckoutFormData(evt), true);
    this.setHeaderCartIconAccessibility();
    this.openIfRedirectedFromCartPage();
    this.watchForStaleCart();
    this.noticeItemsDroppedByMarketSwitch();
  }

  /*
   * Check out submits #CartDrawer-Form, which carries one positional
   * `updates[]` input per rendered line. Quantity edits are already applied
   * over AJAX as they happen, so those inputs only ever matter when the
   * drawer is STALE — and then they overwrite the real cart (a drawer showing
   * qty 1 posts updates[]=1 and checkout silently drops the quantity the
   * shopper set elsewhere). Drop them. The one legitimate case — a quantity
   * typed and not yet sent (still inside cart.js's 300 ms debounce) — is
   * re-sent keyed by line-item key, which can't land on the wrong line.
   * (Without JS this handler never runs and the no-JS form works as before.)
   */
  onCheckoutFormData(evt) {
    const form = evt.target;
    if (!form || form.id !== 'CartDrawer-Form' || !evt.formData) return;
    evt.formData.delete('updates[]');
    form.querySelectorAll('.cart-item[data-key] .quantity__input').forEach((input) => {
      if (input.value === input.defaultValue) return;
      const row = input.closest('.cart-item[data-key]');
      const qty = parseInt(input.value, 10);
      if (row && !isNaN(qty) && qty >= 0) evt.formData.append(`updates[${row.dataset.key}]`, String(qty));
    });
  }

  /* ---------- stale-drawer resync ----------
   * The drawer is server-rendered with the page, and nothing refreshed it
   * when the cart changed somewhere else: another tab, checkout, or a page
   * the shopper then left with Back (the back/forward cache restores the old
   * DOM as-is). The stale drawer showed wrong totals and free-shipping
   * progress, and its Check out posted the stale quantities (see
   * onCheckoutFormData). Resync when the page could have gone stale:
   *   - pageshow from the back/forward cache: always;
   *   - the tab becomes visible again after another tab of ours changed the
   *     cart (a localStorage ping — see announceCartChange), or after being
   *     hidden for a minute with a non-empty cart (checkout or an app in
   *     another tab doesn't ping);
   *   - another tab changes the cart while this one is visible.
   * A resync is ONE request for the drawer + bubble sections, and the DOM is
   * only touched when the cart actually differs (data-cart-sig).
   */
  watchForStaleCart() {
    const PING_KEY = 'eb:cart-changed';
    const LONG_AWAY_MS = 60 * 1000;
    let pingedWhileHidden = false;
    let hiddenAt = 0;

    // Back/forward: from the bfcache (persisted) or re-served from the HTTP cache
    // (persisted is false but the navigation type is back_forward, e.g. mobile
    // Chromium). Either way the rendered cart can be stale.
    window.addEventListener('pageshow', (evt) => {
      let backForward = false;
      try {
        const nav = performance.getEntriesByType('navigation')[0];
        backForward = !!nav && nav.type === 'back_forward';
      } catch (e) {}
      if (evt.persisted || backForward) this.resync({ force: true });
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      const longAway = hiddenAt > 0 && Date.now() - hiddenAt >= LONG_AWAY_MS && this.renderedItemCount() > 0;
      if (pingedWhileHidden || longAway) this.resync({ force: pingedWhileHidden });
      pingedWhileHidden = false;
      hiddenAt = 0;
    });

    window.addEventListener('storage', (evt) => {
      if (evt.key !== PING_KEY) return;
      if (document.visibilityState === 'hidden') pingedWhileHidden = true;
      else this.resync({ force: true });
    });

    // Tell our other tabs whenever THIS tab changes the cart. Every mutation
    // path either publishes cartUpdate or dispatches cart-drawer:rerender.
    const announceCartChange = () => {
      try {
        localStorage.setItem(PING_KEY, `${Date.now()}:${Math.random()}`);
      } catch (e) {
        /* storage unavailable (private mode) — tabs fall back to the timer */
      }
    };
    document.addEventListener('cart-drawer:rerender', (evt) => {
      const source = evt.detail && evt.detail.source;
      // Pure re-reads change nothing; announcing them would ping-pong.
      if (source === 'resync' || source === 'sale-event-refresh') return;
      announceCartChange();
    });
    const subscribeToCartUpdates = () => {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return false;
      subscribe(PUB_SUB_EVENTS.cartUpdate, announceCartChange);
      return true;
    };
    if (!subscribeToCartUpdates()) document.addEventListener('DOMContentLoaded', subscribeToCartUpdates);
  }

  cartState(root) {
    return (root || this).querySelector('[data-cart-state]');
  }

  renderedItemCount() {
    const state = this.cartState();
    return state ? parseInt(state.dataset.itemCount, 10) || 0 : 0;
  }

  /*
   * Re-read the cart-drawer + cart-icon-bubble sections and swap them in if
   * the cart differs from what's rendered. Returns a promise of true when the
   * DOM changed. Runs through the shared cart queue (cart.js) so a read can't
   * land between a mutation and its render; `inQueue` is for callers already
   * running inside that queue (the quantity error path), which would
   * otherwise wait on themselves. Unforced calls are throttled to one per 10s.
   *
   * An in-queue call never reuses (or becomes) the shared resyncPromise: that
   * promise belongs to a resync waiting in the queue — possibly BEHIND the
   * very task asking — so awaiting it from inside the queue deadlocked every
   * later cart change in the tab (a cross-tab ping queued a resync while a
   * quantity change was in flight, then the change was rejected). The queued
   * one still runs afterwards; it's a no-op when data-cart-sig matches.
   */
  resync({ force = false, inQueue = false } = {}) {
    if (!window.routes || !window.routes.cart_url) return Promise.resolve(false);
    if (this.resyncPromise && !inQueue) return this.resyncPromise;
    const now = Date.now();
    if (!force && now - (this.lastResyncAt || 0) < 10000) return Promise.resolve(false);
    this.lastResyncAt = now;

    const task = () =>
      fetch(`${window.routes.cart_url}?sections=cart-drawer,cart-icon-bubble`, {
        // No Accept: application/json header: with it Shopify returns the cart
        // object instead of the rendered sections, and the resync did nothing.
        credentials: 'same-origin',
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((sections) => (sections ? this.applyFreshSections(sections) : false))
        .catch((e) => {
          console.error('cart-drawer: resync failed', e);
          return false;
        });

    if (inQueue) return task();
    const run = window.ebCartMutex ? window.ebCartMutex(task) : task();
    this.resyncPromise = run.then((changed) => {
      this.resyncPromise = null;
      return changed;
    });
    return this.resyncPromise;
  }

  applyFreshSections(sections) {
    const drawerHTML = sections['cart-drawer'];
    if (!drawerHTML) return false;
    const freshInner = new DOMParser().parseFromString(drawerHTML, 'text/html').querySelector('#CartDrawer .drawer__inner');
    const inner = this.querySelector('.drawer__inner');
    if (!freshInner || !inner) return false;

    const freshState = this.cartState(freshInner);
    const currentState = this.cartState(inner);
    if (freshState && currentState && freshState.dataset.cartSig === currentState.dataset.cartSig) return false;

    const hadFocus = this.contains(document.activeElement);
    inner.innerHTML = freshInner.innerHTML;
    const count = freshState ? parseInt(freshState.dataset.itemCount, 10) || 0 : 0;
    this.classList.toggle('is-empty', count === 0);

    const bubbleHTML = sections['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubbleHTML && bubble) {
      const freshBubble = new DOMParser().parseFromString(bubbleHTML, 'text/html').querySelector('.shopify-section');
      if (freshBubble) bubble.innerHTML = freshBubble.innerHTML;
    }

    // Recommendations re-hydrate; sale-event.js re-reads its stamps. Never
    // publish cartUpdate here: nothing changed in the cart, only our view.
    document.dispatchEvent(new CustomEvent('cart-drawer:rerender', { detail: { source: 'resync' } }));
    this.refocusAfterRender(hadFocus);
    return true;
  }

  /*
   * Focus after a .drawer__inner swap. The swap removes the focused control
   * (focus falls to <body>, behind the open drawer), and trapFocus (global.js)
   * cached the first/last focusables when it was set up — they are detached
   * now, so Tab walked straight out of the dialog. Every path that replaces
   * .drawer__inner calls this afterwards: the quantity path (cart.js has its
   * own equivalent), resync, subscriptions, recommendation adds and the gift
   * reconcile.
   *
   * hadFocusInDrawer: whether focus was inside the drawer BEFORE the swap
   *   (capture it before disabling the control — a disabled control drops
   *   focus too). Mouse-only shoppers who never focused the drawer are left
   *   alone, as the quantity path does.
   * successors: a selector, or a list tried in order, for where focus should
   *   land when the focused control didn't survive the swap (e.g. the same
   *   line's subscription control). Falls back to the close button.
   * Focus that survived the swap (e.g. on .drawer__inner itself) stays put;
   * the trap is re-armed either way, on the same container open() uses.
   */
  refocusAfterRender(hadFocusInDrawer, successors) {
    if (!hadFocusInDrawer || !this.classList.contains('active') || typeof trapFocus !== 'function') return;
    const inner = this.querySelector('.drawer__inner');
    if (!inner) return;
    try {
      const active = document.activeElement;
      let target = active && active !== document.body && this.contains(active) ? active : null;
      [].concat(successors || []).some((selector) => {
        if (target) return true;
        try {
          target = this.querySelector(selector);
        } catch (e) {
          /* invalid selector — try the next one */
        }
        return !!target;
      });
      target = target || this.querySelector('.drawer__close') || inner;
      const container = this.classList.contains('is-empty') ? inner : document.getElementById('CartDrawer') || inner;
      trapFocus(container, target);
    } catch (e) {
      console.error('cart-drawer: refocus failed', e);
    }
  }

  /*
   * A market switch (header selector, footer shelf or a geo redirect) makes
   * Shopify drop lines that aren't sold in the new market — silently. Compare
   * the item count against the last page we rendered in a DIFFERENT market
   * and, if lines disappeared, say so at the top of the drawer.
   *
   * The {market, count} snapshot is rewritten after every cart change on this
   * page, not only at page load: a shopper who arrived with an empty cart,
   * added something and then switched market left a snapshot of count 0, so
   * the dropped lines were never mentioned. cartUpdate carries the cart's
   * item_count when the publisher sent a full cart (/cart/change.js); an
   * /cart/add.js response doesn't, so then — and after every
   * cart-drawer:rerender — the rendered count stamp is read once the swap
   * has landed (product-form swaps in a requestAnimationFrame, hence two).
   */
  noticeItemsDroppedByMarketSwitch() {
    const KEY = 'eb:cart-market-seen';
    const market = (window.Shopify && window.Shopify.country) || '';
    const count = this.renderedItemCount();
    let previous = null;
    try {
      previous = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      sessionStorage.setItem(KEY, JSON.stringify({ market, count }));
    } catch (e) {
      return;
    }
    const remember = (itemCount) => {
      try {
        sessionStorage.setItem(KEY, JSON.stringify({ market, count: itemCount }));
      } catch (e) {
        /* storage unavailable — the notice just can't fire */
      }
    };
    const rememberRendered = () =>
      requestAnimationFrame(() => requestAnimationFrame(() => remember(this.renderedItemCount())));
    document.addEventListener('cart-drawer:rerender', () => remember(this.renderedItemCount()));
    const subscribeToCartUpdates = () => {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return false;
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const cartData = event && event.cartData;
        if (cartData && typeof cartData.item_count === 'number') remember(cartData.item_count);
        else rememberRendered();
      });
      return true;
    };
    if (!subscribeToCartUpdates()) document.addEventListener('DOMContentLoaded', subscribeToCartUpdates);

    if (!previous || !market || !previous.market || previous.market === market) return;
    if (!(previous.count > count)) return;
    const header = this.querySelector('.drawer__header');
    if (!header) return;
    const notice = document.createElement('p');
    notice.className = 'cart-drawer__market-notice';
    notice.setAttribute('role', 'status');
    notice.textContent =
      "Some items in your cart aren't sold in the region you switched to, so they were removed.";
    header.insertAdjacentElement('afterend', notice);
  }

  // /cart visits (e.g. the checkout header's cart icon, whose destination is
  // fixed by Shopify) redirect home with this flag set — see layout/theme.liquid.
  // The drawer is the only cart surface, so open it on arrival.
  // The redirect also appends #open-cart (when the /cart URL had no hash of
  // its own), for browsers where sessionStorage is unavailable and the flag
  // never got written. The hash is removed again — path and query kept, so
  // ?utm_* still reach attribution — so a reload or a shared link doesn't
  // reopen the drawer.
  openIfRedirectedFromCartPage() {
    let shouldOpen = false;
    try {
      if (sessionStorage.getItem('eb:open-cart-drawer')) {
        sessionStorage.removeItem('eb:open-cart-drawer');
        shouldOpen = true;
      }
    } catch (e) {
      /* sessionStorage unavailable (private mode) — the #open-cart hash covers it */
    }
    if (window.location.hash === '#open-cart') {
      shouldOpen = true;
      try {
        history.replaceState(history.state, '', window.location.pathname + window.location.search);
      } catch (e) {
        /* history unavailable — the hash just stays */
      }
    }
    if (shouldOpen) setTimeout(() => this.open(), 300);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        // The empty state renders inside .drawer__inner like everything else
        // (Dawn's .drawer__inner-empty no longer exists). Trapping on a
        // missing element threw, which left focus on the page and made Esc
        // (a keyup listener on this element) unable to close the drawer.
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    // Robust scroll lock (see body.cart-drawer-open in base.css). overflow:hidden
    // alone lets iOS touch-drag scroll the page behind the drawer, especially when
    // the item list is short enough not to scroll internally. Pin the body with
    // position:fixed and remember the scroll offset, restored on close so the page
    // doesn't jump. The guard avoids re-saving scrollY (as 0) on a redundant open.
    // On desktop the CSS rule is media-gated off, so body.style.top is a no-op
    // (top has no effect on a static body) and the close-time scrollTo matches the
    // unchanged position — i.e. this is inert on desktop.
    if (!document.body.classList.contains('cart-drawer-open')) {
      this.lockedScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = `-${this.lockedScrollY}px`;
      document.body.classList.add('cart-drawer-open');
    }
  }

  close(event) {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    if (document.body.classList.contains('cart-drawer-open')) {
      document.body.classList.remove('cart-drawer-open');
      document.body.style.top = '';
      window.scrollTo(0, this.lockedScrollY || 0);
    }

    /* Mouse close (overlay click, mouse-clicked X): blur the cart-icon
       trigger so it doesn't paint a :focus-visible ring. Chrome's
       heuristic carries :focus-visible state forward across the
       programmatic .focus() in removeTrapFocus when the user has
       pressed any key recently — e.g. Tab'd through items inside the
       drawer — even though the close itself was a mouse click.
       Keyboard close (Esc keyup, or Enter/Space on the X button which
       fires a click with detail===0) skips this branch and keeps the
       ring, which is the visible cue the focus has returned. */
    const isMouseClose = event && event.type === 'click' && event.detail > 0;
    if (isMouseClose && this.activeElement && typeof this.activeElement.blur === 'function') {
      this.activeElement.blur();
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    // INP optimization: open the drawer immediately (so the user sees the
    // slide-in animation start within a frame of clicking ATC) and defer the
    // heavy DOMParser + innerHTML replacement to the next animation frame.
    // Without this, parsing a 20-item cart's HTML synchronously can spike
    // the click handler's TBT to 80-150ms, making the ATC click feel laggy.
    this.open();

    requestAnimationFrame(() => {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
